'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import parse from 'html-react-parser';

import {
    Checkbox,
    CheckboxProps,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {
    TriStateCheckbox,
    TriStateCheckboxProps,
    TriStateCheckboxChangeEvent
} from 'primereact/tristatecheckbox';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {StoreState} from '~/frontend/src/store';

type CheckboxInputProps = Omit<CheckboxProps, 'autoComplete' | 'onChange' | 'key'>;
const checkboxInputProps = keys<CheckboxInputProps>();
type TriStateCheckboxInputProps = Omit<TriStateCheckboxProps, 'autoComplete' | 'onChange' | 'key'>;
const triStateCheckboxInputProps = keys<TriStateCheckboxInputProps>();
type InputProps = Omit<CheckboxInputProps | TriStateCheckboxInputProps, 'checked'>;

type InputChangeProps = CheckboxChangeEvent;

type TriInputChangeProps = TriStateCheckboxChangeEvent;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The label for the field.
     */
    checkboxLabel? : string;

    /**
     * Is the checkbox a tri state one.
     */
    triState? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    dontDisplayFalseValue? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    checkboxLabel        : '',
    triState             : true,
    markDirty            : false,
    dontDisplayFalseValue: false,
    inputClassNames      : [],
    inputStyle           : {}
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : boolean | null;

    /**
     * The value to determine dirty against.
     */
    originalValue? : boolean;

    onChange : ( value : boolean | null, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;

// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const BooleanField = React.forwardRef<Checkbox | TriStateCheckbox, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  checkboxLabel,
                  displayValue,
                  displayClassNames,
                  dontDisplayFalseValue,
                  id,
                  inputClassNames,
                  inputStyle,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  name,
                  onChange,
                  originalValue,
                  pt,
                  triState,
                  uid,
                  value
              }                                 = props,
              elementId                         = id ?? `${uid}_booleanField`,
              valueCurrent                      = useRef<boolean | undefined>( value ),
              elementValueCurrent               = useRef<boolean | undefined>( value ),
              [ elementValue, setElementValue ] = useState<boolean | undefined>( value ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : parse( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '' );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              booleanStateClassName : string    = useMemo(
                  () => {
                      let retVal = 'yn-booleanfield-undefined';

                      if ( elementValue === undefined && displayValue ) {
                          if ( displayValue === 'true' ) {
                              retVal = `yn-booleanfield-${displayClassNames.join( ' ' ).indexOf( 'property-inherited' ) !== -1 ? 'inherited' : 'default'}-true`;
                          }
                          else if ( displayValue === 'false' ) {
                              retVal = `yn-booleanfield-${displayClassNames.join( ' ' ).indexOf( 'property-inherited' ) !== -1 ? 'inherited' : 'default'}-false`;
                          }
                      }
                      else if ( elementValue === true ) {
                          retVal = 'yn-booleanfield-true';
                      }
                      else if ( elementValue === false ) {
                          retVal = !dontDisplayFalseValue ? 'yn-booleanfield-false' : '';
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      displayValue,
                      dontDisplayFalseValue
                  ]
              ),
              onInputChange                     = useCallback(
                  ( changedValue : boolean | null, event : any ) => {
                      if ( changedValue !== elementValueCurrent.current ) {
                          setElementValue( changedValue ?? undefined );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      displayValue,
                      onChange
                  ]
              ),
              onTriStateInputChange             = useCallback(
                  ( e : TriInputChangeProps ) => {
                      // if no current value toggle default value (displayValue) otherwise use event value (convert undefined to null)
                      const newValue = elementValueCurrent.current === undefined && displayValue === 'true'
                          ? false
                          : e.value === undefined
                              ? null
                              : e.value;

                      onInputChange( newValue, e );
                  },
                  [
                      onInputChange
                  ]
              ),
              onCheckboxInputChange             = useCallback(
                  ( e : InputChangeProps ) => {
                      // if no current value toggle default value (displayValue) otherwise use event value
                      const newValue = elementValueCurrent.current === undefined && displayValue === 'true'
                          ? false
                          : !!e.checked;

                      onInputChange( newValue, e );
                  },
                  [
                      onInputChange
                  ]
              ),
              getInput                          = () : React.ReactElement => {
                  let retVal : React.ReactElement;

                  if ( !triState ) {
                      retVal = <Checkbox
                          {..._.pick( props, checkboxInputProps ) as CheckboxInputProps}

                          id={elementId}
                          name={name}

                          checked={!!elementValue}
                          onChange={onCheckboxInputChange}

                          className={`yn-booleanfield ${inputClassNames.concat( booleanStateClassName ).join( ' ' )}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                          tooltip={undefined}
                          tooltipOptions={undefined}

                          aria-describedby={`${elementId}_message`}
                      />;
                  }
                  else {
                      retVal = <TriStateCheckbox
                          {..._.pick( props, triStateCheckboxInputProps ) as TriStateCheckboxInputProps}

                          id={elementId}
                          name={name}

                          value={elementValue}
                          onChange={onTriStateInputChange}

                          className={`yn-booleanfield ${inputClassNames.concat( booleanStateClassName ).join( ' ' )}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle }}

                          tooltip={undefined}
                          tooltipOptions={undefined}

                          aria-describedby={`${elementId}_message`}
                      />;
                  }

                  return retVal;
              }

        valueCurrent.current = value;
        elementValueCurrent.current = elementValue;

        useEffect(
            () => setElementValue( value ),
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && value !== originalValue}
            displayValue={undefined}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <div className="yn-booleanfield-wrapper field-checkbox">
                {getInput()}
                <label htmlFor={elementId} className="p-checkbox-label">{messageResolver( checkboxLabel )}</label>
            </div>
        </FieldWrapper>;
    }
} );
BooleanField.defaultProps = defaultProps;
BooleanField.displayName = 'BooleanField';

export default BooleanField;
